/**
 * MyQuizPage = "My Profile"
 *
 * Email-only login that handles three flavors of returning visitors:
 *   1. Completed the full RV Match application → redirect to /my-matches/:sessionId
 *      (existing behavior preserved).
 *   2. Only unlocked one or more free tools → show a "My Tools" panel listing
 *      everything they have access to (free + paid via VIP).
 *   3. Brand-new email → friendly empty state with a CTA to take the application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Mail, Truck, ArrowRight, Calculator, FileSearch, MessageSquare,
  CheckCircle2, Sparkles, ArrowLeft, Heart,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reusable per-tool meta (kept in sync with FreeToolPage TOOLS registry)
const TOOL_META = {
  cost_calculator: {
    name: 'Cost Calculator',
    href: '/free/cost-calculator',
    Icon: Calculator,
    accent: '#22c55e',
    blurb: '5-year cost of ownership',
  },
  deal_analyzer: {
    name: 'Deal Analyzer',
    href: '/free/deal-analyzer',
    Icon: FileSearch,
    accent: '#3b82f6',
    blurb: 'Is this listing a good deal?',
  },
  negotiation_coach: {
    name: 'Negotiation Coach',
    href: '/free/negotiation-coach',
    Icon: MessageSquare,
    accent: '#a855f7',
    blurb: 'Word-for-word dealership script',
  },
};

const MyQuizPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // After successful lookup we render the profile panel
  const [profile, setProfile] = useState(null); // { name, unlocked_tools, has_full_profile, session_id }

  // Auto-attempt lookup if email is in URL (?email=...) or in localStorage cache
  useEffect(() => {
    const queryEmail = searchParams.get('email');
    const cached = localStorage.getItem('rv_free_tool_email');
    const initial = queryEmail || cached;
    if (initial) {
      setEmail(initial);
      // Slight delay so users see the form once before auto-resolve
      lookupEmail(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookupEmail = async (lookupAddress) => {
    const target = (lookupAddress || email).trim().toLowerCase();
    if (!target) {
      toast.error('Please enter your email');
      return;
    }
    setIsLoading(true);

    try {
      // free-tools/profile handles both kinds of users:
      //   • full quiz/application completers (returns has_full_profile=true + session_id → we redirect to matches)
      //   • free-tool-only users (returns unlocked_tools list → we render the My Tools panel)
      const profileRes = await axios.get(`${API}/free-tools/profile`, {
        params: { email: target },
      });
      if (profileRes.data?.success) {
        if (profileRes.data.has_full_profile && profileRes.data.session_id) {
          toast.success(`Welcome back, ${profileRes.data.name || 'friend'}!`);
          navigate(`/my-matches/${profileRes.data.session_id}`);
          return;
        }
        // Cache email so subsequent visits auto-resolve
        localStorage.setItem('rv_free_tool_email', target);
        setProfile(profileRes.data);
        toast.success(`Welcome, ${profileRes.data.name || 'friend'}!`);
        return;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      // 404 = no profile — friendly message + nudge to take application
      if (err.response?.status === 404 || detail.toLowerCase().includes('no profile')) {
        toast.error("We couldn't find a profile with that email. Try filling out the application first!");
      } else {
        toast.error(detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    lookupEmail();
  };

  const handleSwitchEmail = () => {
    setProfile(null);
    setEmail('');
    localStorage.removeItem('rv_free_tool_email');
  };

  // Profile view (after successful lookup)
  if (profile) {
    const tools = (profile.unlocked_tools || []).filter((t) => TOOL_META[t]);
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-gray-50 to-white'}`} data-testid="my-profile-panel">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="mb-6 flex items-center justify-between">
            <button
              onClick={handleSwitchEmail}
              className={`flex items-center gap-1 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
              data-testid="switch-email-btn"
            >
              <ArrowLeft size={14} /> Use a different email
            </button>
          </div>

          <div className={`rounded-2xl p-6 sm:p-8 mb-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-[#F2A900]/20 flex items-center justify-center">
                <Heart size={22} className="text-[#F2A900]" />
              </div>
              <div>
                <h1 className={`text-2xl sm:text-3xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="profile-greeting">
                  Welcome, {profile.name || 'friend'}!
                </h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{profile.email}</p>
              </div>
            </div>
          </div>

          {/* My Tools */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-[#F2A900]" />
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>My Tools</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                {tools.length}
              </span>
            </div>

            {tools.length === 0 ? (
              <div
                className={`rounded-2xl p-6 text-center ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
                data-testid="my-tools-empty"
              >
                <p className={`mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  You don't have any tools yet.
                </p>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Take the full RV Match application to get personalized RV matches and access to your profile.
                </p>
                <Button
                  onClick={() => navigate('/')}
                  className="bg-[#F2A900] hover:bg-[#D99700] text-black font-bold"
                  data-testid="take-application-btn"
                >
                  Find Your Perfect RV <ArrowRight size={16} className="ml-1" />
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="my-tools-grid">
                {tools.map((slug) => {
                  const meta = TOOL_META[slug];
                  const Icon = meta.Icon;
                  return (
                    <Link
                      key={slug}
                      to={meta.href}
                      className={`group rounded-2xl p-5 flex items-start gap-4 transition-all hover:-translate-y-0.5 ${isDark ? 'bg-gray-800 hover:bg-gray-750 ring-1 ring-gray-700' : 'bg-white hover:shadow-lg ring-1 ring-gray-200'}`}
                      data-testid={`my-tool-${slug}`}
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                        style={{ background: `${meta.accent}20`, color: meta.accent }}
                      >
                        <Icon size={22} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{meta.name}</h3>
                          <CheckCircle2 size={14} className="text-green-500" />
                        </div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{meta.blurb}</p>
                        <div
                          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold"
                          style={{ color: meta.accent }}
                        >
                          Open tool <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* CTA: take full application if not yet */}
          {!profile.has_full_profile && (
            <div
              className={`rounded-2xl p-5 sm:p-6 ${isDark ? 'bg-gradient-to-r from-[#004E86]/40 to-[#F2A900]/20 ring-1 ring-[#F2A900]/30' : 'bg-gradient-to-r from-[#004E86]/5 to-[#F2A900]/10 ring-1 ring-[#F2A900]/30'}`}
              data-testid="take-application-cta"
            >
              <div className="flex items-start gap-4">
                <Truck size={28} className="text-[#F2A900] shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className={`font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Want personalized RV matches?
                  </h3>
                  <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Fill out the 4-step RV Match application and we'll show you the exact RVs that fit your budget, lifestyle, and tow setup.
                  </p>
                  <Button
                    onClick={() => navigate('/')}
                    className="bg-[#F2A900] hover:bg-[#D99700] text-black font-bold"
                  >
                    Find My Match <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Email lookup form
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-[#004E86] to-[#003d6b]'}`} data-testid="my-quiz-page">
      <div className="py-16 px-4">
        <div className="max-w-xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#F2A900] mb-6">
            <Truck className="text-black" size={40} />
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            data-testid="page-title"
          >
            My Profile
          </h1>
          <p className="text-lg text-blue-100 mb-8">
            Enter your email to view your matches.
          </p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pb-16">
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl overflow-hidden`}>
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className={`flex items-center gap-2 mb-2 ${isDark ? 'text-gray-300' : ''}`}>
                  <Mail size={16} />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  data-testid="my-quiz-email"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#F2A900] hover:bg-[#D99700] text-black font-bold uppercase tracking-wide py-4 text-lg"
                data-testid="my-quiz-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Looking up your profile...
                  </>
                ) : (
                  <>
                    Open My Profile <ArrowRight className="ml-2" size={20} />
                  </>
                )}
              </Button>
            </form>

            <div className={`mt-6 pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} text-center`}>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Haven't filled out the application yet?
              </p>
              <Button
                variant="link"
                onClick={() => navigate('/')}
                className="text-[#004E86] hover:text-[#003d6b] font-semibold"
                data-testid="take-quiz-link"
              >
                Find Your Perfect RV <ArrowRight className="ml-1" size={16} />
              </Button>
            </div>
          </div>
        </div>

        <p className={`mt-4 text-center text-xs ${isDark ? 'text-gray-500' : 'text-blue-200'}`}>
          No password needed — we'll find you using just your email.
        </p>
      </div>
    </div>
  );
};

export default MyQuizPage;
